import React, { useEffect, useRef, useState } from 'react';

import { Button } from 'primereact/button';
import { Divider } from 'primereact/divider';
import { Dropdown } from 'primereact/dropdown';
import { InputNumber } from 'primereact/inputnumber';
import { InputSwitch } from 'primereact/inputswitch';
import { InputText } from 'primereact/inputtext';
import { Tooltip } from 'primereact/tooltip';
import {
  FieldErrors,
  UseFormRegister,
  UseFormSetValue,
  UseFormWatch
} from 'react-hook-form';
import { FiasObject } from 'types/interfaces';

import { BaseFiasUrl } from 'constants/BaseUrl';
import FiasSearchInput from 'ui/custom-controls/FiasSearchInput';

import {
  FiasHouseExclusionDialog,
  FiasStreetExclusionDialog
} from './FiasAddressExclusionDialogs';

function isValidFiasObject(o: FiasObject | null | undefined): boolean {
  return !!o && typeof o.objectId === 'number' && o.objectId > 0;
}

interface LocationTabContentProps {
  isFias: boolean;
  register: UseFormRegister<any>;
  setValue: UseFormSetValue<any>;
  watch: UseFormWatch<any>;
  errors: FieldErrors<any>;
  houses: FiasObject[];
  fetchHouses: (streetId: number, cityId?: number) => void;
  inputsClassNames?: string;
  errorsClassNames?: string;
  withoutCoordinates?: boolean;
  extraContent?: React.ReactNode;
  enableAddressExclusions?: boolean;
  isMobile?: boolean
}

const LocationTabContent: React.FC<LocationTabContentProps> = ({
  isFias,
  register,
  setValue,
  watch,
  errors,
  houses,
  fetchHouses,
  inputsClassNames,
  errorsClassNames,
  withoutCoordinates,
  extraContent,
  enableAddressExclusions,
  isMobile = false
}) => {
  const cityFias = watch('cityFias');
  const streetFias = watch('streetFias');
  const houseFias = watch('houseFias');
  const longitude = watch('longitude');
  const latitude = watch('latitude');
  const excludedStreetsFias = watch('excludedStreetsFias') as FiasObject[] | undefined;
  const excludedHousesFias = watch('excludedHousesFias') as FiasObject[] | undefined;

  const [streetExclusionOpen, setStreetExclusionOpen] = useState(false);
  const [houseExclusionOpen, setHouseExclusionOpen] = useState(false);

  const prevCityIdRef = useRef<number | undefined>(undefined);
  const prevStreetIdRef = useRef<number | undefined>(undefined);

  const exclusionMode = !!enableAddressExclusions && isFias;
  const showCityStreetExclusion =
    exclusionMode &&
    isValidFiasObject(cityFias) &&
    !isValidFiasObject(streetFias) &&
    !isValidFiasObject(houseFias);
  const showStreetHouseExclusion =
    exclusionMode &&
    isValidFiasObject(cityFias) &&
    isValidFiasObject(streetFias) &&
    !isValidFiasObject(houseFias);

  useEffect(() => {
    if (cityFias && streetFias) {
      fetchHouses(streetFias.objectId, cityFias.objectId);
    }
  }, [cityFias, streetFias]);

  useEffect(() => {
    if (!exclusionMode) return;
    const cid = isValidFiasObject(cityFias) ? cityFias!.objectId : undefined;
    const sid = isValidFiasObject(streetFias) ? streetFias!.objectId : undefined;

    if (
      prevCityIdRef.current !== undefined &&
      cid !== prevCityIdRef.current
    ) {
      setValue('excludedStreetsFias', [], { shouldDirty: true });
      setValue('excludedHousesFias', [], { shouldDirty: true });
    }
    if (
      prevStreetIdRef.current !== undefined &&
      sid !== prevStreetIdRef.current
    ) {
      setValue('excludedHousesFias', [], { shouldDirty: true });
    }
    prevCityIdRef.current = cid;
    prevStreetIdRef.current = sid;
  }, [exclusionMode, cityFias, streetFias, setValue]);

  useEffect(() => {
    if (!exclusionMode) return;
    if (isValidFiasObject(streetFias)) {
      setValue('excludedStreetsFias', [], { shouldDirty: true });
    }
  }, [exclusionMode, streetFias?.objectId, setValue]);

  useEffect(() => {
    if (!exclusionMode) return;
    if (isValidFiasObject(houseFias)) {
      setValue('excludedHousesFias', [], { shouldDirty: true });
    }
  }, [exclusionMode, houseFias?.objectId, setValue]);

  const streetsTooltip =
    excludedStreetsFias?.length ?
      'Кроме ' + excludedStreetsFias.map((s) => s.fullName || s.name).join(', ')
      : 'Исключить определенные улицы';

  const housesTooltip =
    excludedHousesFias?.length ?
      'Кроме ' + excludedHousesFias.map((h) => h.fullName || h.name).join(', ')
      : 'Исключить определенные дома';

  const streetTooltip = (
    <>
      <i
        id="street-tooltip"
        className="pi pi-question-circle ml-2"
        style={{ cursor: 'pointer' }}
      />
      <Tooltip target="#street-tooltip" content="Улица или название СНТ" />
    </>
  );

  return (
    <div 
      className="location-tab-content"
      style={isMobile ? { 
        maxHeight: '40vh',
        overflow: 'auto',
        paddingRight: '4px' 
      } : {}}
    >
      <div className="flex flex-column gap-3 pb-0">
        <div className="flex flex-column">
          <div className="flex flex-column">
            <div className="flex flex-column">
              <div className="flex align-items-center gap-3">
                <InputSwitch
                  checked={isFias}
                  onChange={(e) => {
                    const newIsFias = e.value;
                    setValue('isFias', newIsFias!);

                    if (newIsFias) {
                    // Переход на ФИАС: сброс ручных полей
                      setValue('customCity', '');
                      setValue('customStreet', '');
                      setValue('customHouse', '');
                      // Сброс полей ФИАС
                      setValue('cityFias', null);
                      setValue('streetFias', null);
                      setValue('houseFias', null);
                      setValue('excludedStreetsFias', []);
                      setValue('excludedHousesFias', []);
                    } else {
                    // Переход на ручной ввод: предзаполнение из ФИАС
                      setValue(
                        'customCity',
                        cityFias?.fullName || cityFias?.name || ''
                      );
                      setValue('customStreet', streetFias?.name || '');
                      setValue('customHouse', houseFias?.name || '');
                      // Сброс полей ФИАС
                      setValue('cityFias', null);
                      setValue('streetFias', null);
                      setValue('houseFias', null);
                      setValue('excludedStreetsFias', []);
                      setValue('excludedHousesFias', []);
                    }
                  }}
                />

                <label htmlFor="isFias">ФИАС Адрес</label>
              </div>
              <p
                className={`${errorsClassNames} card-errors ${
                  errors.isFias ? 'text-red-500' : 'text-transparent'
                }`}
              >
                {errors.isFias?.message || ' '}
              </p>

              {isFias ? (
                <>
                  <div className="flex flex-column">
                    <div className="flex flex-row justify-content-between align-items-center gap-3">
                      <label style={{ width: '103.69px' }}>
                      Населенный пункт
                      </label>
                      <div className="flex flex-row align-items-center gap-2 flex-grow-1 min-w-0">
                        <FiasSearchInput
                          key="cityFias"
                          value={cityFias!}
                          fieldName="cityFias"
                          setValue={setValue}
                          placeholder="Начните вводить населенный пункт..."
                          searchUrl={`${BaseFiasUrl}/city`}
                          dependentFields={['streetFias', 'houseFias']}
                          isCity
                          className={`${inputsClassNames ?? ''} w-full flex-grow-1 min-w-0`}
                        />
                        {showCityStreetExclusion && (
                          <Button
                            type="button"
                            icon="pi pi-pencil"
                            className="p-button-rounded p-button-text flex-shrink-0"
                            onClick={() => setStreetExclusionOpen(true)}
                            tooltip={streetsTooltip}
                            tooltipOptions={{ position: 'top' }}
                          />
                        )}
                      </div>
                    </div>
                    <p
                      className={`${errorsClassNames} card-errors ${
                        errors.cityFias ? 'text-red-500' : 'text-transparent'
                      }`}
                    >
                      {errors.cityFias?.message || ' '}
                    </p>
                  </div>

                  <div className="flex flex-column">
                    <div className="flex flex-row justify-content-between align-items-center gap-3">
                      <label style={{ minWidth: '103.69px' }}>
                      Улица
                        {streetTooltip}
                      </label>
                      <div className="flex flex-row align-items-center gap-2 flex-grow-1 min-w-0">
                        <FiasSearchInput
                          key="streetFias"
                          value={streetFias!}
                          fieldName="streetFias"
                          setValue={setValue}
                          parentId={cityFias?.objectId}
                          placeholder="Начните вводить улицу..."
                          searchUrl={`${BaseFiasUrl}/city/{parentId}/street`}
                          disabled={!cityFias || cityFias.objectId === 0}
                          dependentFields={['houseFias']}
                          className={`${inputsClassNames ?? ''} w-full flex-grow-1 min-w-0`}
                        />
                        {showStreetHouseExclusion && (
                          <Button
                            type="button"
                            icon="pi pi-pencil"
                            className="p-button-rounded p-button-text flex-shrink-0"
                            onClick={() => setHouseExclusionOpen(true)}
                            tooltip={housesTooltip}
                            tooltipOptions={{ position: 'top' }}
                          />
                        )}
                      </div>
                    </div>
                    <p
                      className={`${errorsClassNames} card-errors ${
                        errors.streetFias ? 'text-red-500' : 'text-transparent'
                      }`}
                    >
                      {errors.streetFias?.message || ' '}
                    </p>
                  </div>

                  {/* Дом */}
                  <div className="flex flex-column">
                    <div className="flex flex-row justify-content-between align-items-center gap-3">
                      <label style={{ minWidth: '103.69px' }}>Дом</label>
                      <Dropdown
                        dataKey="objectId"
                        value={houseFias}
                        options={houses}
                        optionLabel="name"
                        onChange={(e) =>
                          setValue('houseFias', e.value, { shouldDirty: true })
                        }
                        disabled={!streetFias || streetFias.objectId === 0}
                        onShow={() => {
                          if (streetFias) {
                            fetchHouses(streetFias!.objectId);
                          }
                        }}
                        filter
                        showClear
                        className="w-full"
                      />
                    </div>
                    <p
                      className={`${errorsClassNames} card-errors ${
                        errors.houseFias ? 'text-red-500' : 'text-transparent'
                      }`}
                    >
                      {errors.houseFias?.message || ' '}
                    </p>
                  </div>
                </>
              ) : (
                <>
                  <div className="flex flex-column">
                    <div className="flex flex-row justify-content-between align-items-center gap-3">
                      <label style={{ minWidth: '103.69px' }}>
                      Населенный пункт
                      </label>
                      <InputText
                        {...register('customCity')}
                        placeholder="Населенный пункт"
                        autoComplete={JSON.stringify(watch('customCity'))}
                        className={`${inputsClassNames ?? ''} w-full flex-grow`}
                      />
                    </div>
                    <p
                      className={`${errorsClassNames} card-errors ${
                        errors.customCity ? 'text-red-500' : 'text-transparent'
                      }`}
                    >
                      {errors.customCity?.message || ' '}
                    </p>
                  </div>

                  <div className="flex flex-column">
                    <div className="flex flex-row justify-content-between align-items-center gap-3">
                      <label style={{ minWidth: '103.69px' }}>
                      Улица
                        {streetTooltip}
                      </label>
                      <InputText
                        {...register('customStreet')}
                        placeholder="Улица"
                        autoComplete={JSON.stringify(watch('customStreet'))}
                        className={`${inputsClassNames ?? ''} w-full flex-grow`}
                      />
                    </div>
                    <p
                      className={`${errorsClassNames} card-errors ${
                        errors.customStreet ? 'text-red-500' : 'text-transparent'
                      }`}
                    >
                      {errors.customStreet?.message || ' '}
                    </p>
                  </div>

                  <div className="flex flex-column">
                    <div className="flex flex-row justify-content-between align-items-center gap-3">
                      <label style={{ minWidth: '103.69px' }}>Дом</label>
                      <InputText
                        {...register('customHouse')}
                        placeholder="Дом"
                        autoComplete={JSON.stringify(watch('customHouse'))}
                        className={`${inputsClassNames ?? ''} w-full flex-grow`}
                      />
                    </div>
                    <p
                      className={`${errorsClassNames} card-errors ${
                        errors.customHouse ? 'text-red-500' : 'text-transparent'
                      }`}
                    >
                      {errors.customHouse?.message || ' '}
                    </p>
                  </div>
                </>
              )}

              <Divider />

              <div className="flex flex-column">
                <div className="flex flex-row justify-content-between align-items-center gap-3">
                  <label style={{ minWidth: '103.69px' }}>Регион/область</label>
                  <InputText
                    className={`${inputsClassNames ?? ''} w-full flex-grow`}
                    {...register('region')}
                  />
                </div>
                <p
                  className={`${errorsClassNames} card-errors ${
                    errors.region ? 'text-red-500' : 'text-transparent'
                  }`}
                >
                  {errors.region?.message || ' '}
                </p>
              </div>

              <div className="flex flex-column">
                <div className="flex flex-row justify-content-between align-items-center gap-3">
                  <label style={{ minWidth: '103.69px' }}>Округ</label>
                  <InputText
                    className={`${inputsClassNames ?? ''} w-full flex-grow`}
                    {...register('county')}
                  />
                </div>
                <p
                  className={`${errorsClassNames} card-errors ${
                    errors.county ? 'text-red-500' : 'text-transparent'
                  }`}
                >
                  {errors.county?.message || ' '}
                </p>
              </div>

              <div className="flex flex-column">
                <div className="flex flex-row justify-content-between align-items-center gap-3">
                  <label style={{ minWidth: '103.69px' }}>Район</label>
                  <InputText
                    className={`${inputsClassNames ?? ''} w-full flex-grow`}
                    {...register('district')}
                  />
                </div>
                <p
                  className={`${errorsClassNames} card-errors ${
                    errors.district ? 'text-red-500' : 'text-transparent'
                  }`}
                >
                  {errors.district?.message || ' '}
                </p>
              </div>
              {!withoutCoordinates && (
                <>
                  <div className="flex flex-column">
                    <div className="flex flex-row justify-content-between align-items-center gap-3">
                      <label style={{ minWidth: '103.69px' }}>Широта</label>
                      <InputNumber
                        maxFractionDigits={14}
                        locale="en"
                        useGrouping={false}
                        value={latitude}
                        onValueChange={(e) => {
                          if (e.value !== undefined) {
                            setValue('latitude', e.value, {
                              shouldDirty: true,
                              shouldValidate: true
                            });
                          }
                        }}
                        onBlur={(e) => {
                          const value = parseFloat(e.currentTarget.value);
                          if (!isNaN(value)) {
                            setValue('latitude', value, {
                              shouldDirty: true,
                              shouldValidate: true
                            });
                          }
                        }}
                        className={`${inputsClassNames ?? ''} w-full flex-grow`}
                      />
                    </div>
                    <p
                      className={`${errorsClassNames} card-errors ${
                        errors.latitude ? 'text-red-500' : 'text-transparent'
                      }`}
                    >
                      {errors.latitude?.message || ' '}
                    </p>
                  </div>

                  <div className="flex flex-column">
                    <div className="flex flex-row justify-content-between align-items-center gap-3">
                      <label style={{ minWidth: '103.69px' }}>Долгота</label>
                      <InputNumber
                        maxFractionDigits={14}
                        locale="en"
                        useGrouping={false}
                        value={longitude}
                        onValueChange={(e) => {
                          if (e.value !== undefined) {
                            setValue('longitude', e.value, {
                              shouldDirty: true,
                              shouldValidate: true
                            });
                          }
                        }}
                        onBlur={(e) => {
                          const value = parseFloat(e.currentTarget.value);
                          if (!isNaN(value)) {
                            setValue('longitude', value, {
                              shouldDirty: true,
                              shouldValidate: true
                            });
                          }
                        }}
                        className={`${inputsClassNames ?? ''} w-full flex-grow`}
                      />
                    </div>
                    <p
                      className={`${errorsClassNames} card-errors ${
                        errors.longitude ? 'text-red-500' : 'text-transparent'
                      }`}
                    >
                      {errors.longitude?.message || ' '}
                    </p>
                  </div>
                </>
              )}

              {extraContent ? <>{extraContent}</> : null}
            </div>
          </div>
        </div>
      </div>

      {exclusionMode && cityFias?.objectId ? (
        <FiasStreetExclusionDialog
          visible={streetExclusionOpen}
          onHide={() => setStreetExclusionOpen(false)}
          cityId={cityFias.objectId}
          initialExcluded={excludedStreetsFias ?? []}
          onApply={(streets) =>
            setValue('excludedStreetsFias', streets, { shouldDirty: true })
          }
        />
      ) : null}

      {exclusionMode && cityFias?.objectId && streetFias?.objectId ? (
        <FiasHouseExclusionDialog
          visible={houseExclusionOpen}
          onHide={() => setHouseExclusionOpen(false)}
          cityId={cityFias.objectId}
          streetId={streetFias.objectId}
          initialExcluded={excludedHousesFias ?? []}
          onApply={(houses) =>
            setValue('excludedHousesFias', houses, { shouldDirty: true })
          }
        />
      ) : null}
    </div>
  );
};

export default LocationTabContent;
